import { useState, useEffect } from "react";
import { Calendar, Clock, MapPin, Users, CheckCircle2, Circle, Timer, Plus, Edit, Trash2, Package } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddEventDialog from "@/components/AddEventDialog";
import EditEventDialog from "@/components/EditEventDialog";
import { eventService } from "@/services/event.service";
import { vendorService } from "@/services/vendor.service";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { permissions } from "@/lib/mockAuth";
import { canAddResource, isTrialExpired } from "@/utils/trialUtils";
import { BlockedActionDialog } from "@/components/BlockedActionDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const TimelineModule = ({ weddingId, wedding, onEventDeleted }) => {
  const { userRole, activeOrg } = useAuth();
  const canManage = permissions.canManageEvents(userRole);
  const isReadOnly = permissions.isReadOnly(userRole);
  
  const [events, setEvents] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);

  // Check trial limits
  const eventLimitCheck = canAddResource(wedding, 'events', events.length);
  const isBlocked = !eventLimitCheck.canAdd || isTrialExpired(wedding);

  // Load events and vendors
  useEffect(() => {
    loadData();
  }, [weddingId, activeOrg]);

  const loadData = async () => {
    if (!weddingId || !activeOrg?.id) return;
    
    try {
      setLoading(true);
        const [eventsData, vendorsData] = await Promise.all([
          eventService.getEvents(weddingId, activeOrg.id),
          vendorService.getVendors(weddingId, activeOrg.id)
        ]);
      setEvents(eventsData);
      setAllVendors(vendorsData);
    } catch (error) {
      console.error('Failed to load timeline data:', error);
      toast.error('Failed to load timeline data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare wedding venue for default location
  console.log('TimelineModule - wedding object:', wedding);
  console.log('TimelineModule - wedding.venue:', wedding?.venue);
  console.log('TimelineModule - wedding.venue_location:', wedding?.venue_location);
  
  // If full venue_location exists, use it. Otherwise create a simple object with just the name
  const weddingVenue = wedding?.venue ? {
    name: wedding.venue,
    address: wedding.venue_location?.address || wedding.venue,
    location: wedding.venue_location?.location || null,
    placeId: wedding.venue_location?.place_id || null
  } : null;
  
  console.log('TimelineModule - constructed weddingVenue:', weddingVenue);

  // Sort events by date and time
  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA - dateB;
  });

  const handleAddEvent = async (newEvent) => {
    try {
      await eventService.createEvent(weddingId, activeOrg.id, newEvent);
      await loadData();
    } catch (error) {
      console.error('Failed to add event:', error);
      throw error; // Re-throw so AddEventDialog can handle the error
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setIsEditDialogOpen(true);
  };

  const handleUpdateEvent = async (updatedEvent) => {
    try {
      await eventService.updateEvent(updatedEvent.id, updatedEvent);
      await loadData();
      toast.success("Event updated successfully");
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async () => {
    if (deletingEvent) {
      // Check if there are confirmed vendors for this event
      const vendorsForEvent = getVendorsForEvent(deletingEvent.name);
      const confirmedVendors = vendorsForEvent.filter(v => v.status === 'confirmed');
      
      // If confirmed vendors exist, show additional warning
      if (confirmedVendors.length > 0) {
        // This will be handled by the AlertDialog with enhanced warning
        // The deletion will proceed if user confirms
      }
      
      try {
        await eventService.deleteEvent(deletingEvent.id);
        await loadData();
        
        if (confirmedVendors.length > 0) {
          toast.warning(
            `Event "${deletingEvent.name}" deleted. ${confirmedVendors.length} confirmed vendor(s) will have this event removed from their profile.`,
            { duration: 5000 }
          );
        } else {
          toast.success(`Event "${deletingEvent.name}" deleted successfully`);
        }
        
        setDeletingEvent(null);
        // Notify parent that an event was deleted so vendors can refresh
        if (onEventDeleted) {
          onEventDeleted();
        }
      } catch (error) {
        console.error('Failed to delete event:', error);
        toast.error('Failed to delete event');
      }
    }
  };

  // Get vendors for a specific event
  const getVendorsForEvent = (eventName) => {
    return allVendors.filter(vendor => {
      if (!vendor.events) return false;
      
      // Exact match or partial match (e.g., "Wedding" matches "Wedding Ceremony")
      return vendor.events.some(vendorEvent => {
        const normalizedEventName = eventName.toLowerCase();
        const normalizedVendorEvent = vendorEvent.toLowerCase();
        
        return normalizedEventName.includes(normalizedVendorEvent) || 
               normalizedVendorEvent.includes(normalizedEventName);
      });
    });
  };

  // Handle vendor selection for an event
  // Vendor selection functionality removed

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-6 w-6 text-success" />;
      case "ongoing":
        return <Timer className="h-6 w-6 text-warning" />;
      default:
        return <Circle className="h-6 w-6 text-primary" />;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-foreground">Event Timeline</h3>
        {canManage && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    onClick={() => setIsAddDialogOpen(true)}
                    className="gradient-primary"
                    disabled={isBlocked}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Event
                  </Button>
                </span>
              </TooltipTrigger>
              {isBlocked && (
                <TooltipContent>
                  <p>🔒 Trial limit reached<br />Upgrade for more events</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {loading ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">Loading events...</p>
        </Card>
      ) : sortedEvents.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No events scheduled</h3>
          <p className="text-muted-foreground mb-4">Events will appear here once they are added</p>
          {canManage && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={() => setIsAddDialogOpen(true)}
                      className="gradient-primary"
                      disabled={isBlocked}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Event
                    </Button>
                  </span>
                </TooltipTrigger>
                {isBlocked && (
                  <TooltipContent>
                    <p>🔒 Trial limit reached<br />Upgrade for more events</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </Card>
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-border hidden sm:block" />
          
          <div className="space-y-6">
            {sortedEvents.map((event) => (
              <div key={event.id} className="relative flex gap-4">
                {/* Timeline Dot */}
                <div className="hidden sm:flex items-start pt-1 z-10">
                  <div className="p-2 rounded-full gradient-primary">
                    {getStatusIcon(event.status)}
                  </div>
                </div>

                {/* Event Card */}
                <Card className="flex-1 p-5 transition-smooth hover:shadow-medium">
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <h4 className="text-xl font-bold text-foreground">{event.name}</h4>
                        <span className="text-sm text-muted-foreground capitalize">{event.status}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 text-accent" />
                        <span>{event.time}</span>
                      </div>
                      {event.location?.name && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4 text-gold" />
                          <span className="truncate">{event.location.name}</span>
                        </div>
                      )}
                      {event.attendees > 0 && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-4 w-4 text-success" />
                          <span>{event.attendees} guests expected</span>
                        </div>
                      )}
                    </div>

                    {event.description && (
                      <p className="text-sm text-muted-foreground pt-2 border-t">{event.description}</p>
                    )}

                    {/* Vendors Finalized Section */}
                    {/* Vendors Assigned to Event */}
                    {(() => {
                      const availableVendors = getVendorsForEvent(event.name);
                      const confirmedVendors = availableVendors.filter(v => v.status === 'confirmed');
                      const confirmedVendorIds = confirmedVendors.map(v => v.id);
                      const manuallySelectedIds = event.selected_vendors || [];
                      const allSelectedIds = [...new Set([...confirmedVendorIds, ...manuallySelectedIds])];
                      const selectedVendorsList = allVendors.filter(v => allSelectedIds.includes(v.id));
                      
                      return selectedVendorsList.length > 0 && (
                        <div className="pt-3 border-t">
                          <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Package className="h-4 w-4 text-primary" />
                            Confirmed Vendors
                          </Label>
                          
                          <div className="flex flex-wrap gap-2 mt-2">
                            {selectedVendorsList.map(vendor => (
                              <Badge 
                                key={vendor.id} 
                                variant="secondary"
                                className="text-xs"
                              >
                                {vendor.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Action Buttons */}
                    {canManage && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditEvent(event)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => setDeletingEvent(event)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Event Dialog */}
      <AddEventDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAddEvent={handleAddEvent}
        weddingVenue={weddingVenue}
      />

      {/* Edit Event Dialog */}
      <EditEventDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        event={editingEvent}
        onUpdateEvent={handleUpdateEvent}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingEvent} onOpenChange={() => setDeletingEvent(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const vendorsForEvent = deletingEvent ? getVendorsForEvent(deletingEvent.name) : [];
                const confirmedVendors = vendorsForEvent.filter(v => v.status === 'confirmed');
                return confirmedVendors.length > 0 
                  ? "⚠️ Warning: Confirmed Vendors Found"
                  : "Delete Event?";
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {(() => {
                const vendorsForEvent = deletingEvent ? getVendorsForEvent(deletingEvent.name) : [];
                const confirmedVendors = vendorsForEvent.filter(v => v.status === 'confirmed');
                
                if (confirmedVendors.length > 0) {
                  return (
                    <>
                      <p>
                        You are about to delete <strong>{deletingEvent?.name}</strong>. This event has <strong>{confirmedVendors.length} confirmed vendor(s)</strong> assigned to it.
                      </p>
                      <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
                        <p className="text-sm font-medium text-warning-foreground mb-2">
                          ⚠️ Important:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                          <li>This event will be removed from all vendor profiles</li>
                          <li>Confirmed vendors may have contracts or payments associated</li>
                          <li>Consider unconfirming vendors first if needed</li>
                        </ul>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm font-medium mb-1">Affected vendors:</p>
                        <div className="flex flex-wrap gap-2">
                          {confirmedVendors.map(vendor => (
                            <Badge key={vendor.id} variant="secondary" className="text-xs">
                              {vendor.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground pt-2">
                        This action cannot be undone. Are you sure you want to proceed?
                      </p>
                    </>
                  );
                } else {
                  return (
                    <p>
                      Are you sure you want to delete <strong>{deletingEvent?.name}</strong>? This action cannot be undone.
                    </p>
                  );
                }
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(() => {
                const vendorsForEvent = deletingEvent ? getVendorsForEvent(deletingEvent.name) : [];
                const confirmedVendors = vendorsForEvent.filter(v => v.status === 'confirmed');
                return confirmedVendors.length > 0 ? "Delete Anyway" : "Delete";
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BlockedActionDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        resourceType="events"
        limit={eventLimitCheck.limit}
        wedding={wedding}
        onUpgradeSuccess={loadData}
      />
    </div>
  );
};

export default TimelineModule;
